import React, { useEffect, useRef, useState } from 'react';
import { QuizButton, QuizButtonAsInput } from '~/ui/buttons/QuizButton';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { ANSWER_BUTTON } from '../itemIds';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import audioEffectClick from '~/assets/mp3/light_switches.mp3';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useQuizStore } from '~/training/sequence/templates/items/quiz/QuizStore';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { shallow } from 'zustand/shallow';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';
import styled, { css } from 'styled-components';
import LightButton from '~/editor/buttons/LightButton';
import TrashIcon from '~/assets/icons/trash.svg';
import { Switcher } from '~/editor/buttons/Switcher';
import { AudioPlayerButton } from './BigPlayButton';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';

export const TrashIconForBtn = styled(TrashIcon)``;

const DraggableElement = styled.div<{ disableButtons?: boolean }>`
  width: 100%;
  justify-content: flex-start;
  display: flex;
  align-items: flex-start;
  margin-left: ${p => (p.disableButtons ? '-28px' : '0px')};
  padding-left: ${p => (p.disableButtons ? '16px' : '25px')};
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;

  &.drag-over-top {
    border-top: 2px solid #9845fb !important;
    border-bottom: 2px solid transparent !important;
  }

  &.drag-over-bottom {
    border-top: 2px solid transparent !important;
    border-bottom: 2px solid #9845fb !important;
  }

  .drag-icon {
    margin-left: -40px;
    margin-right: 8px;
    margin-top: 22px;
    cursor: move;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const WrapperForAudioPlayer = styled.div<{ notActiveAudioPlayer?: boolean }>`
  .audioPlayerButtonDiv {
    width: 50px !important;
    height: 50px !important;
    margin-left: 10px;

    .drillBoxAnimation {
      width: 45px !important;
      height: 45px !important;
      border-radius: 10px;

      display: none !important;
    }

    .playButton {
      border-radius: 10px !important;
      min-width: 40px !important;
      width: 40px !important;
      height: 40px !important;
    }
  }

  ${p =>
    p.notActiveAudioPlayer &&
    css`
      .audioPlayerButtonDiv {
        .playButton {
          background-color: #f8f1ff !important;

          svg {
            fill: #9738ff !important;
          }
        }

        .playButton:hover {
          background-color: #9738ff !important;

          svg {
            fill: #fff !important;
          }
        }
      }
    `}
`;

interface Props {
  answer: string | boolean;
  group: string;
  text?: string;
  correct?: boolean;
  feedback?: string;

  /** Id of an audio element that needs to be played for the button to be enabled */
  linkedAudioId?: string;
  feedbackAudios?: {
    id?: string | number;
    mediaUrl: string;
    subtitlesUrl: string;
    voiceId?: string;
  };
  llamaSurvey?: boolean;
  question?: string;
  correctAnswers?: [];
  disableButtons?: boolean;
}

/** Short hash from question text for sessionStorage key (avoids QuotaExceededError). */
function questionStorageKey(
  question: string | undefined,
  group: string
): string {
  const input = question ?? group;
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return `quiz_rxd_${Math.abs(h).toString(36)}`;
}

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset', 'loadMedia');

export type AnswerButtonDefinition = ItemDefinition<
  typeof ANSWER_BUTTON,
  Props
>;

const selector = (s: TrainingState) =>
  select(s, 'saveQuestionAnswers', 'status');

export class AnswerButton extends Item<typeof ANSWER_BUTTON, Props> {
  protected render({
    answer,
    group,
    text,
    correct,
    children,
    feedback,
    feedbackAudios,
    linkedAudioId,
    id,
    llamaSurvey = false,
    question,
    correctAnswers,
    disableButtons
  }: RenderProps<Props>) {
    const { saveQuestionAnswers, status } = useTrainingStore(selector);
    const { reset, loadMedia } = useMediaPlayerStore(mediaSelector, shallow);
    const {
      addBlocker,
      removeBlocker,
      audioPlayed,
      setAnswer,
      clearAnswer,
      setLastAnswer
    } = useSequenceStore();
    const lms = useLmsAdapter();
    const { setQuizFeedback, getQuizFeedback } = useQuizStore();
    const [disabledButtons, setDisabledButtons] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState('default');
    const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
    const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
    const [playAudioEffectClick] = useAudioEffect(audioEffectClick);
    const {
      editorMode,
      contentEditable,
      isReadOnlyView,
      updateSequence,
      updateSequenceInStore,
      sequence,
      removeSubItem,
      apiService
    } = useEditorStore();
    const [isCorrect, setIsCorrect] = useState<string>(
      answer ? 'true' : 'false'
    );
    const [showFormForFeedback, setShowFormForFeedback] = useState<boolean>(
      feedback ? true : false
    );

    const [classNameForDragOver, setClassNameForDragOver] =
      useState<string>('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [textValue, setTextValue] = useState<string>(text || '');
    const [showDeleteButton, setShowDeleteButton] = useState<boolean>(false);
    const [localFeedback, setLocalFeedback] = useState(feedback || '');

    const globalMedia = useGlobalMediaStore();
    const globalPlayer = globalMedia?.player;

    useEffect(() => {
      if (globalPlayer) {
        setDisabledButtons(true);
      } else {
        setDisabledButtons(false);
      }
    }, [globalPlayer]);

    useEffect(() => {
      if (!sequence) return;

      // Find the QUESTION that actually contains THIS answer button, not just
      // the first QUESTION in the sequence. Carousel templates (Cards6, Cards7,
      // Multimedia4_Quiz) have several QUESTION cards, so relying on the first
      // one made every card's delete buttons follow the first card's count.
      const question =
        sequence.items.find(
          item =>
            item.type === 'QUESTION' &&
            item.items?.some((sub: { id: string }) => sub.id == id)
        ) ?? sequence.items.find(item => item.type === 'QUESTION');

      //don't allow to remove question button (keep at least 2 options)
      setShowDeleteButton(
        !!(question && question.items && question.items.length > 2)
      );
    }, [sequence, id]);

    useGlobalMedia();

    useEffect(() => {
      if (answer == correct && selectedAnswer == 'default') {
        addBlocker(group);
        return () => removeBlocker(group);
      }
    }, [selectedAnswer]);

    const click = () => {
      //if it editor mode, we don't need to do nothing
      if (editorMode) {
        return false;
      }

      if (status !== 'completed') {
        //Save question answers
        saveQuestionAnswers(id);
      }

      if (selectedAnswer == 'default') {
        reset();

        if (!llamaSurvey) {
          //maybe we need to rollback this
          setQuizFeedback(feedback, group);
          // ENG-817: Multimedia4_Quiz template issues
          // setQuizFeedback(feedback);
        }

        setAnswer(group, id);
        removeBlocker(group);

        if (feedbackAudios && feedbackAudios.mediaUrl) {
          loadMedia(
            feedbackAudios.mediaUrl,
            feedbackAudios?.subtitlesUrl,
            'audio',
            true,
            0
          );
        }

        if (shouldSendInteractions() && lms.isAvailable) {
          // Hash question to short key to avoid QuotaExceededError with long question text
          const storageKey = questionStorageKey(question, group);

          let userSelectedAnswers: string[] = [];
          try {
            userSelectedAnswers = JSON.parse(
              sessionStorage.getItem(storageKey) || '[]'
            );
          } catch {
            userSelectedAnswers = [];
          }

          if (userSelectedAnswers.includes(id)) {
            userSelectedAnswers = userSelectedAnswers.filter(
              (item: string) => item !== id
            );
          } else {
            userSelectedAnswers.push(id);
          }

          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify(userSelectedAnswers)
            );
          } catch (e) {
            if (e instanceof DOMException && e.name === 'QuotaExceededError') {
              // Storage full or key/value too large: clear this key and retry once, then give up
              try {
                sessionStorage.removeItem(storageKey);
                sessionStorage.setItem(
                  storageKey,
                  JSON.stringify(userSelectedAnswers)
                );
              } catch {
                // Skip persisting; LMS flow continues without storage
              }
            }
          }

          if (correctAnswers && correctAnswers.length > 1) {
            const containsAll = correctAnswers.every(element =>
              userSelectedAnswers.includes(element)
            );

            if (userSelectedAnswers.length >= correctAnswers.length) {
              if (containsAll) {
                try {
                  sessionStorage.removeItem(storageKey);
                } catch {
                  // ignore
                }

                lms.recordMultipleChoiceInteraction(
                  group,
                  correctAnswers,
                  true,
                  correctAnswers,
                  question
                );
              } else {
                lms.recordMultipleChoiceInteraction(
                  group,
                  userSelectedAnswers,
                  false,
                  correctAnswers,
                  question
                );
              }
            }
          } else {
            lms.recordTrueFalseInteraction(
              group,
              text || answer,
              answer == correct,
              true,
              question
            );
          }
        }

        if (answer == correct) {
          if (!llamaSurvey) {
            try {
              playAudioEffectRightAnswer();
            } catch (error) {
              // Silently ignore audio effect errors
            }
          }
          setSelectedAnswer('correct');
          setLastAnswer('correct');
        } else {
          if (!llamaSurvey) {
            try {
              playAudioEffectWrongAnswer();
            } catch (error) {
              // Silently ignore audio effect errors
            }
          }
          setSelectedAnswer('wrong');
          setLastAnswer('wrong');
        }

        if (llamaSurvey) {
          try {
            playAudioEffectClick();
          } catch (error) {
            // Silently ignore audio effect errors
          }
        }
      } else {
        //Button is selected and we need to return the state back
        setSelectedAnswer('default');
        setLastAnswer('default');
        const currentFeedback = getQuizFeedback(group);
        if (feedback == currentFeedback) setQuizFeedback('', group);
        clearAnswer(group, id);
      }
      // Keep focus on button for keyboard users (accessibility)
      setTimeout(() => {
        if (buttonRef.current && document.contains(buttonRef.current)) {
          buttonRef.current.focus();
        }
      }, 0);
    };

    const content = text ? text : children;

    const handleDelete = () => {
      removeSubItem(id);
    };

    const SetAnswerForButton = (value: string) => {
      setIsCorrect(value);
      updateSequence(id, 'correct', value == 'true' ? true : false);
    };

    const [focused, setFocused] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const answerInputRef = useRef<HTMLTextAreaElement>(null);
    const feedbackInputRef = useRef<HTMLTextAreaElement>(null);

    // Grow the multi-line field to fit its content (CAT-499) instead of
    // scrolling. Reset to `auto` first so it can also shrink.
    const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };

    useEffect(() => {
      if (contentEditable) {
        autoResizeTextarea(answerInputRef.current);
      }
    }, [textValue, contentEditable]);

    useEffect(() => {
      if (showFormForFeedback) {
        autoResizeTextarea(feedbackInputRef.current);
      }
    }, [localFeedback, showFormForFeedback]);

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      let newText = event.target.value;

      if (newText.trim() == '') {
        newText = 'Type Here';
        event.target.innerHTML = newText;
        setTextValue(newText);
      }

      updateSequence(id, 'title', newText, 'QUESTION_FIELD');
    };

    const MAX_ANSWER_LENGTH = 255;

    const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const target = event.currentTarget;
      let newText = target.value || '';

      if (newText.trim() == '') {
        newText = ' ';
        target.value = newText;
        setTextValue(newText);
        target.setSelectionRange(1, 1);
      } else {
        if (newText.length > MAX_ANSWER_LENGTH) {
          newText = newText.slice(0, MAX_ANSWER_LENGTH);
          target.value = newText;
          target.setSelectionRange(MAX_ANSWER_LENGTH, MAX_ANSWER_LENGTH);
        }
        setTextValue(newText || '');
      }

      autoResizeTextarea(target);
    };

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (
        contentEditable &&
        event.currentTarget.value?.toLowerCase().trim() == 'type here'
      ) {
        const element = event.currentTarget;
        element.value = '';
        element.focus();
        element.setSelectionRange(0, 0);
        setTextValue('');
      }
      setFocused(true);
    };

    const AddExplanation = () => {
      setShowFormForFeedback(true);

      const feedback =
        isCorrect == 'true' ? 'It is correct' : 'It is incorrect';

      setLocalFeedback(feedback);
      updateSequence(id, 'feedback', feedback);
    };

    const RemoveExplanation = () => {
      setShowFormForFeedback(false);
      updateSequence(id, 'feedback', '');
      updateSequence(id, 'audio_ids', []);
      updateSequenceInStore(id, 'feedbackAudios', null);
    };

    const handleFeedbackChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setLocalFeedback(e.target.value);
      autoResizeTextarea(e.currentTarget as HTMLTextAreaElement);
    };

    const handleFeedbackBlur = () => {
      updateSequence(id, 'feedback', localFeedback);
      setFocused(false);
    };

    const handleFeedbackFocus = () => {
      setFocused(true);
    };

    // Helper function to find QUESTION that contains the element with given id
    const findQuestionForElement = (elementId: string) => {
      if (!sequence) return null;

      // Find QUESTION that contains this element in its items
      for (const item of sequence.items) {
        if (item.type === 'QUESTION' && item.items) {
          const hasElement = item.items.some(
            (subItem: { id: string }) => subItem.id == elementId
          );
          if (hasElement) {
            return item;
          }
        }
      }

      // Fallback: return first QUESTION if not found (for non-carousel cases)
      return sequence.items.find(item => item.type === 'QUESTION');
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      if (disableButtons) return;

      e.dataTransfer.setData('text/plain', id);

      if (window) {
        window.sessionStorage.setItem('dragItemId', id);
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      if (disableButtons) return;
      e.preventDefault();
      setIsDragOver(false);
      const dragItemId = e.dataTransfer.getData('text/plain');

      if (!sequence) return;

      // Find QUESTION that contains the drop target element
      const questionItem = findQuestionForElement(id);
      if (!questionItem || !questionItem.items) return;

      // Verify that drag element is also in the same QUESTION
      const dragInSameQuestion = questionItem.items.some(
        (item: { id: string }) => item.id == dragItemId
      );

      if (!dragInSameQuestion) {
        // Elements are in different QUESTIONS (different carousel cards)
        return;
      }

      const dragIndex = questionItem.items.findIndex(
        (item: { id: string }) => item.id == dragItemId
      );
      const dropIndex = questionItem.items.findIndex(
        (item: { id: string }) => item.id == id
      );

      if (dragIndex === -1 || dropIndex === -1) return;

      const dragElement = document.querySelector(`[data-id="${dragItemId}"]`);
      const dropElement = document.querySelector(`[data-id="${id}"]`);

      if (!dragElement || !dropElement) return;

      const parent = dragElement.parentElement;
      if (!parent) return;

      if (dropIndex > dragIndex) {
        try {
          parent.insertBefore(dragElement, dropElement.nextSibling);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      } else {
        try {
          parent.insertBefore(dragElement, dropElement);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      }

      const newItems = [...questionItem.items];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);

      // Update only the specific QUESTION that contains these elements
      const updatedSequence = {
        ...sequence,
        items: sequence.items.map(item =>
          item.type === 'QUESTION' && item.id == questionItem.id
            ? { ...item, items: newItems }
            : item
        )
      };

      newItems.forEach((item, index) => {
        if (item.id == dragItemId) {
          apiService?.reorderSubItem(item.id, index + 1);
        }
      });

      Object.assign(sequence, updatedSequence);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      if (disableButtons) return;

      e.preventDefault();
      setIsDragOver(true);

      // debugger;

      if (window) {
        const dragItemId = window.sessionStorage.getItem('dragItemId');
        const currentId = e.currentTarget.dataset.id;

        if (!dragItemId || !currentId) return;

        // Find QUESTION that contains the current element
        const questionItem = findQuestionForElement(currentId);
        if (!questionItem || !questionItem.items) return;

        // Verify that drag element is also in the same QUESTION
        const dragInSameQuestion = questionItem.items.some(
          (item: { id: string }) => item.id == dragItemId
        );

        if (!dragInSameQuestion) {
          // Elements are in different QUESTIONS (different carousel cards)
          setClassNameForDragOver('');
          return;
        }

        const dragIndex = questionItem.items.findIndex(
          (item: { id: string }) => item.id == dragItemId
        );
        const dropIndex = questionItem.items.findIndex(
          (item: { id: string }) => item.id == currentId
        );

        if (dragIndex === dropIndex) {
          setClassNameForDragOver('');
        } else {
          setClassNameForDragOver(
            dropIndex > dragIndex ? 'drag-over-bottom' : 'drag-over-top'
          );
        }
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      if (disableButtons) return;

      e.preventDefault();
      setIsDragOver(false);
    };

    let buttonVariant = selectedAnswer;
    if (isReadOnlyView) {
      buttonVariant = answer == correct ? 'correct' : 'wrong';
    } else if (selectedAnswer != 'default' && llamaSurvey) {
      buttonVariant = 'llamaSurvey';
    }

    if (contentEditable) {
      return (
        <DraggableElement
          draggable={!focused && !disableButtons}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          data-id={id}
          data-test-id="answer-option-row"
          className={isDragOver ? classNameForDragOver : ''}
          disableButtons={disableButtons}
        >
          {!disableButtons && (
            <div className="drag-icon" data-test-id="drag-handle">
              <DragAndDropIcon />
            </div>
          )}
          <div style={{ width: '100%' }}>
            <div
              style={{
                width: '100%',
                justifyContent: 'space-between',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <QuizButtonAsInput
                ref={answerInputRef}
                rows={1}
                tabIndex={0}
                maxLength={255}
                onBlur={handleBlur}
                value={textValue}
                onChange={handleInput}
                onFocus={handleFocus}
              />
              <div
                style={{
                  width: '140px',
                  display: 'flex',
                  justifyContent: 'space-around'
                }}
              >
                <Switcher
                  value={isCorrect}
                  onChange={SetAnswerForButton}
                  disabled={disableButtons}
                />
                {showDeleteButton && (
                  <LightButton
                    onClick={handleDelete}
                    style={{ padding: '11px', width: '38px', height: '38px' }}
                    disabled={disableButtons}
                    data-test-id="delete-answer-button"
                  >
                    <TrashIconForBtn
                      style={{ marginLeft: '-5px', marginTop: '-5px' }}
                    />
                  </LightButton>
                )}
              </div>
            </div>
            {disableButtons ? null : showFormForFeedback ? (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '10px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <textarea
                    ref={feedbackInputRef}
                    rows={1}
                    value={localFeedback}
                    onChange={handleFeedbackChange}
                    onBlur={handleFeedbackBlur}
                    onFocus={handleFeedbackFocus}
                    style={{
                      width: '100%',
                      border: '1px solid #000',
                      backgroundColor: '#FFFDE8',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      resize: 'none',
                      overflow: 'hidden',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 1.4
                    }}
                  />
                </div>
                <WrapperForAudioPlayer notActiveAudioPlayer={!feedbackAudios}>
                  <AudioPlayerButton
                    id={`${id}_audios`}
                    mediaUrl={feedbackAudios ? feedbackAudios.mediaUrl : ''}
                    audioId={feedbackAudios ? feedbackAudios.id : ''}
                    clearSubitemAudio={true}
                  />
                </WrapperForAudioPlayer>
                <div
                  style={{ marginLeft: '8px', marginRight: '3px' }}
                  tabIndex={-1}
                >
                  <LightButton
                    onClick={RemoveExplanation}
                    style={{
                      padding: '11px',
                      width: '38px',
                      height: '38px'
                    }}
                  >
                    <TrashIconForBtn
                      style={{ marginLeft: '-5px', marginTop: '-5px' }}
                    />
                  </LightButton>
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '10px'
                }}
              >
                <LightButton
                  onClick={AddExplanation}
                  style={{ padding: '11px' }}
                  data-test-id="add-answer-feedback"
                >
                  Add answer feedback
                </LightButton>
              </div>
            )}
          </div>
        </DraggableElement>
      );
    }

    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <QuizButton
        ref={buttonRef}
        variant={buttonVariant}
        onClick={click}
        disabled={
          disabledButtons || (linkedAudioId && !audioPlayed[linkedAudioId])
        }
        tabIndex={0}
        className="pressable"
        data-test-id="answer-button"
        aria-pressed={selectedAnswer !== 'default'}
      >
        {selectedAnswer == 'wrong' ? (
          <VisuallyHidden role="alert">{t`Incorrect`}</VisuallyHidden>
        ) : null}
        {selectedAnswer == 'correct' ? (
          <VisuallyHidden role="alert">{t`Correct`}</VisuallyHidden>
        ) : null}
        <span>{content}</span>
      </QuizButton>
    );
  }
}

registry[ANSWER_BUTTON] = AnswerButton;
